<script setup lang="ts">
import { useConfirm } from 'primevue/useconfirm'
import {
  getAdminListUserSkillCustomsQueryKey,
  useAdminListUserSkillCustoms,
  useAdminRejectUserSkillCustom,
  useAdminUnrejectUserSkillCustom,
  useListJobCategories,
} from '@swipe4work/api-orval'
import type { AdminListUserSkillCustomUserSkillCustomDto } from '@swipe4work/models-orval'
import { CustomRejectionStatus, SkillType } from '@swipe4work/models-orval'
import { useQueryClient } from '@tanstack/vue-query'
import { useToast } from '@/shared/composables/UseToast'
import PromoteSkillDialog from '../components/PromoteSkillDialog.vue'

definePage({
  path: '/data/user-skill-customs',
  name: '/data/UserSkillCustoms',
  meta: {
    title: 'Custom skills',
  },
})

const skillTypeOptions = [
  {
    label: 'Hard',
    value: SkillType.Hard,
  },
  {
    label: 'Soft',
    value: SkillType.Soft,
  },
  {
    label: 'Taal',
    value: SkillType.Language,
  },
  {
    label: 'Certificaat',
    value: SkillType.Certificate,
  },
  {
    label: 'Overig',
    value: SkillType.Other,
  },
]

function getSkillTypeLabel(type: typeof SkillType[keyof typeof SkillType]): string {
  return skillTypeOptions.find(o => o.value === type)?.label ?? type
}

// Job categories map for resolving IDs to names
const { data: jobCategoriesData } = useListJobCategories()
const jobCategoryMap = computed(() => {
  const map = new Map<number, string>()

  for (const cat of jobCategoriesData.value?.jobCategories ?? []) {
    map.set(cat.id, cat.name)
  }

  return map
})

function getCategoryName(id: number): string {
  return jobCategoryMap.value.get(id) ?? String(id)
}

// Pagination, search & status filter
const search = ref('')
const limit = ref(20)
const offset = ref(0)
const first = ref(0)

// Openstaand (pending) is the default working view; Afgewezen / Alle are opt-in.
const status = ref<CustomRejectionStatus>(CustomRejectionStatus.Pending)
const statusOptions = [
  {
    label: 'Openstaand',
    value: CustomRejectionStatus.Pending,
  },
  {
    label: 'Afgewezen',
    value: CustomRejectionStatus.Rejected,
  },
  {
    label: 'Alle',
    value: CustomRejectionStatus.All,
  },
]

function onStatusChange() {
  offset.value = 0
  first.value = 0
}

const { data: listData, isPending, isError } = useAdminListUserSkillCustoms(
  computed(() => ({
    search: search.value || undefined,
    limit: limit.value,
    offset: offset.value,
    status: status.value,
  })),
)

const userSkillCustoms = computed(() => listData.value?.userSkillCustoms ?? [])
const totalRecords = computed(() => listData.value?.totalCount ?? 0)

let searchTimeout: ReturnType<typeof setTimeout> | null = null

function onSearchInput(value: string | undefined) {
  if (searchTimeout) { clearTimeout(searchTimeout) }
  searchTimeout = setTimeout(() => {
    search.value = value ?? ''
    offset.value = 0
    first.value = 0
  }, 300)
}

function onPage(event: {
  first: number;
  rows: number
}) {
  first.value = event.first
  offset.value = event.first
  limit.value = event.rows
}

const promotingRow = ref<AdminListUserSkillCustomUserSkillCustomDto | null>(null)

// Reject / un-reject: a non-destructive soft-state toggled via the app-wide confirm popup.
const confirm = useConfirm()
const toast = useToast()
const queryClient = useQueryClient()

function invalidateList() {
  queryClient.invalidateQueries({ queryKey: getAdminListUserSkillCustomsQueryKey() })
}

const rejectMutation = useAdminRejectUserSkillCustom({
  mutation: {
    onSuccess: () => {
      toast.success('Afgewezen', 'De custom skill is afgewezen.')
      invalidateList()
    },
    onError: () => {
      toast.error('Fout', 'Er is iets misgegaan bij het afwijzen.')
    },
  },
})

const unrejectMutation = useAdminUnrejectUserSkillCustom({
  mutation: {
    onSuccess: () => {
      toast.success('Hersteld', 'De custom skill staat weer open.')
      invalidateList()
    },
    onError: () => {
      toast.error('Fout', 'Er is iets misgegaan bij het herstellen.')
    },
  },
})

function confirmReject(skill: AdminListUserSkillCustomUserSkillCustomDto) {
  confirm.require({
    header: 'Custom skill afwijzen',
    icon: 'fa-regular fa-ban',
    message: `"${skill.canonicalName}" afwijzen? De skill verdwijnt uit de openstaande lijst en wordt niet meer voorgesteld door de AI. Je kunt dit later herstellen.`,
    acceptLabel: 'Afwijzen',
    rejectLabel: 'Annuleren',
    acceptProps: { severity: 'danger' },
    accept: () => rejectMutation.mutate({ id: skill.id }),
  })
}

function confirmUnreject(skill: AdminListUserSkillCustomUserSkillCustomDto) {
  confirm.require({
    header: 'Custom skill herstellen',
    icon: 'fa-regular fa-rotate-left',
    message: `"${skill.canonicalName}" terugzetten naar openstaand?`,
    acceptLabel: 'Herstellen',
    rejectLabel: 'Annuleren',
    accept: () => unrejectMutation.mutate({ id: skill.id }),
  })
}
</script>

<template>
  <div class="space-y-5">
    <Card>
      <template #content>
        <div class="flex flex-col gap-4">
          <h3 class="text-lg font-semibold">
            Custom skills
          </h3>

          <!-- Search + status filter -->
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <IconField class="w-full sm:max-w-md">
              <InputIcon class="fa-regular fa-search" />
              <InputText
                :model-value="search"
                placeholder="Zoeken op naam..."
                class="w-full"
                @update:model-value="onSearchInput"
              />
            </IconField>

            <SelectButton
              v-model="status"
              :options="statusOptions"
              option-label="label"
              option-value="value"
              :allow-empty="false"
              aria-label="Status filter"
              @change="onStatusChange"
            />
          </div>

          <!-- Table -->
          <div v-if="isPending" class="flex flex-col gap-2">
            <Skeleton v-for="n in 5" :key="n" height="3rem" />
          </div>
          <Message v-else-if="isError" severity="error">
            Er is iets misgegaan bij het laden van de custom skills.
          </Message>
          <DataTable
            v-else
            :value="userSkillCustoms"

            :total-records="totalRecords"
            :first="first"
            :rows="limit"
            :loading="isPending"
            sort-field="canonicalName"
            :sort-order="1"
            class="w-full"
            paginator lazy
            :rows-per-page-options="[10, 20, 50, 100]"
            @page="onPage"
          >
            <Column field="canonicalName" header="Naam" sortable />
            <Column field="normalizedName" header="Genormaliseerd" class="hidden sm:table-cell">
              <template #body="{ data: skill }">
                <span class="text-sm text-surface-500">{{ skill.normalizedName }}</span>
              </template>
            </Column>
            <Column field="skillType" header="Type" sortable>
              <template #body="{ data: skill }">
                <span>{{ getSkillTypeLabel(skill.skillType) }}</span>
              </template>
            </Column>
            <Column field="count" header="Aantal" sortable />
            <Column header="Categorieën">
              <template #body="{ data: skill }">
                <div v-if="skill.jobCategoryIds.length > 0" class="flex flex-wrap gap-1">
                  <Tag
                    v-for="id in skill.jobCategoryIds"
                    :key="id"
                    :value="getCategoryName(id)"
                    severity="secondary"
                  />
                </div>
                <span v-else class="text-sm text-surface-400">—</span>
              </template>
            </Column>
            <Column header="Status" style="width: 120px">
              <template #body="{ data: skill }">
                <Tag
                  v-if="skill.rejectedAt"
                  value="Afgewezen"
                  severity="danger"
                />
                <Tag
                  v-else
                  value="Openstaand"
                  severity="secondary"
                />
              </template>
            </Column>
            <Column header="Acties" style="width: 240px">
              <template #body="{ data: skill }">
                <div class="flex gap-2">
                  <Button
                    label="Promoveren"
                    icon="fa-regular fa-arrow-up-right-dots"
                    size="small"
                    @click="promotingRow = skill"
                  />
                  <Button
                    v-if="!skill.rejectedAt"
                    label="Afwijzen"
                    icon="fa-regular fa-ban"
                    severity="danger"
                    outlined
                    size="small"
                    @click="confirmReject(skill)"
                  />
                  <Button
                    v-else
                    label="Herstellen"
                    icon="fa-regular fa-rotate-left"
                    severity="secondary"
                    size="small"
                    @click="confirmUnreject(skill)"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </template>
    </Card>

    <PromoteSkillDialog v-model:row="promotingRow" />
  </div>
</template>
